// BlogPost.js - Single blog post page with SEO structured data
// Displays full blog post content with author info, reading time, and related posts
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import SEO from '../components/SEO';

// Debug: Log when component renders
console.log('[BlogPost] Component module loaded');

const BlogPost = () => {
    // Get slug from URL params
    const { slug } = useParams();

    // Debug: Log component render
    console.log('[BlogPost] Component rendering with slug:', slug);

    // --- State Variables ---
    const [post, setPost] = useState(null); // Current blog post data
    const [loading, setLoading] = useState(true); // Loading state
    const [error, setError] = useState(null); // Error message
    const [recentPosts, setRecentPosts] = useState([]); // Related/recent posts

    // --- Theming (consistent with site) ---
    const textPrimary = "text-silver-light";
    const textSecondary = "text-silver-medium/80";

    // --- Intersection Observer for Animations ---
    const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [contentRef, contentInView] = useInView({ triggerOnce: true, threshold: 0.1 });

    // --- Fetch Blog Post ---
    const fetchPost = useCallback(async () => {
        console.log(`[BlogPost] Fetching post with slug: ${slug}`);
        setLoading(true);
        setError(null);

        try {
            // Fetch post from API
            const res = await axios.get(`/api/blog/published/${slug}`);
            console.log('[BlogPost] API response:', res.data);

            if (res.data && res.data.success) {
                setPost(res.data.post);
            } else {
                throw new Error(res.data.message || 'Post not found');
            }
        } catch (err) {
            console.error('[BlogPost] Fetch error:', err);
            if (err.response?.status === 404) {
                setError('Blog post not found');
            } else {
                setError(`Error loading blog post: ${err.message}`);
            }
        } finally {
            setLoading(false);
        }
    }, [slug]);

    // --- Fetch Recent Posts ---
    const fetchRecentPosts = useCallback(async () => {
        console.log('[BlogPost] Fetching recent posts...');
        try {
            const res = await axios.get(`/api/blog/recent?limit=3&exclude=${slug}`);
            if (res.data && res.data.success) {
                setRecentPosts(res.data.posts);
                console.log('[BlogPost] Recent posts loaded:', res.data.posts.length);
            }
        } catch (err) {
            console.error('[BlogPost] Recent posts fetch error:', err);
        }
    }, [slug]);

    // --- Initial Data Fetch ---
    useEffect(() => {
        console.log('[BlogPost] Initial mount - fetching post');
        fetchPost();
        fetchRecentPosts();
        // Scroll to top on post change
        window.scrollTo(0, 0);
    }, [fetchPost, fetchRecentPosts]);

    // --- Format Date ---
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // --- Generate Article Schema for SEO ---
    const generateArticleSchema = () => {
        if (!post) return null;

        return {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.metaDescription || post.excerpt || post.title,
            "image": post.featuredImage ? `https://rivieratech.solutions${post.featuredImage}` : "https://rivieratech.solutions/RTSLOGO.png",
            "author": {
                "@type": "Person",
                "name": post.author || "Riviera Tech Solutions"
            },
            "publisher": {
                "@type": "Organization",
                "name": "Riviera Tech Solutions",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://rivieratech.solutions/RTSLOGO.png"
                }
            },
            "datePublished": post.publishedAt || post.createdAt,
            "dateModified": post.updatedAt || post.publishedAt || post.createdAt,
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `https://rivieratech.solutions/blog/${post.slug}`
            },
            "keywords": post.keywords?.join(', ') || '',
            "articleSection": post.category || 'Technology',
            "wordCount": post.content ? post.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0
        };
    };

    // --- Loading State ---
    if (loading) {
        return (
            <div className="bg-black min-h-screen font-sans">
                <div className="container mx-auto px-4 sm:px-6 pt-32 pb-16">
                    <div className="max-w-3xl mx-auto animate-pulse">
                        {/* Back button skeleton */}
                        <div className="h-4 bg-gray-800/50 rounded w-24 mb-8"></div>
                        {/* Title skeleton */}
                        <div className="h-10 bg-gray-800/50 rounded w-3/4 mb-4"></div>
                        <div className="h-10 bg-gray-800/50 rounded w-1/2 mb-6"></div>
                        {/* Meta skeleton */}
                        <div className="flex gap-4 mb-8">
                            <div className="h-4 bg-gray-800/50 rounded w-32"></div>
                            <div className="h-4 bg-gray-800/50 rounded w-24"></div>
                        </div>
                        {/* Image skeleton */}
                        <div className="h-64 bg-gray-800/50 rounded-xl mb-8"></div>
                        {/* Content skeleton */}
                        <div className="space-y-4">
                            <div className="h-4 bg-gray-800/50 rounded w-full"></div>
                            <div className="h-4 bg-gray-800/50 rounded w-full"></div>
                            <div className="h-4 bg-gray-800/50 rounded w-5/6"></div>
                            <div className="h-4 bg-gray-800/50 rounded w-full"></div>
                            <div className="h-4 bg-gray-800/50 rounded w-4/5"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // --- Error State ---
    if (error) {
        return (
            <div className="bg-black min-h-screen font-sans">
                <SEO title="Post Not Found" />
                <div className="container mx-auto px-4 sm:px-6 pt-32 pb-16">
                    <div className="max-w-2xl mx-auto text-center">
                        <svg className="w-20 h-20 mx-auto text-gray-700 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h1 className={`text-2xl font-semibold ${textPrimary} mb-4`}>{error}</h1>
                        <p className={`${textSecondary} mb-6`}>
                            The blog post you're looking for doesn't exist or has been removed.
                        </p>
                        <Link
                            to="/blog"
                            className="inline-flex items-center px-6 py-3 bg-moss hover:bg-moss-light text-black rounded-md transition-colors"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to Blog
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // --- No Post Found ---
    if (!post) {
        return null;
    }

    return (
        <div className="bg-black min-h-screen font-sans">
            {/* SEO Component with Article Schema */}
            <SEO 
                title={post.metaTitle || post.title}
                description={post.metaDescription || post.excerpt || `Read ${post.title} on Riviera Tech Solutions blog.`}
                keywords={post.keywords?.join(', ')}
                image={post.featuredImage}
                url={`/blog/${post.slug}`}
                type="article"
                schema={generateArticleSchema()}
            />

            {/* Article Header */}
            <article>
                <header
                    ref={heroRef}
                    className="pt-28 sm:pt-32 lg:pt-36 pb-8 bg-black"
                >
                    <div className="container mx-auto px-4 sm:px-6">
                        <div className={`max-w-3xl mx-auto transition-all duration-1000 transform ${heroInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                            {/* Back Link */}
                            <Link 
                                to="/blog"
                                className={`inline-flex items-center ${textSecondary} hover:text-moss-light transition-colors mb-6`}
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back to Blog
                            </Link>

                            {/* Category Badge */}
                            {post.category && (
                                <Link 
                                    to={`/blog?category=${encodeURIComponent(post.category)}`}
                                    className="inline-block px-3 py-1 border border-moss/50 text-moss-light text-sm font-medium rounded-full mb-4 hover:border-moss transition-colors"
                                >
                                    {post.category}
                                </Link>
                            )}

                            {/* Title */}
                            <h1 className={`text-3xl sm:text-4xl md:text-5xl font-serif font-medium mb-6 ${textPrimary} leading-tight`}>
                                {post.title}
                            </h1>

                            {/* Meta Info */}
                            <div className="flex flex-wrap items-center gap-4 text-sm mb-6">
                                {/* Author */}
                                <span className={`flex items-center ${textSecondary}`}>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    {post.author}
                                </span>

                                {/* Date */}
                                <span className={`flex items-center ${textSecondary}`}>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    {formatDate(post.publishedAt || post.createdAt)}
                                </span>

                                {/* Reading Time */}
                                <span className={`flex items-center ${textSecondary}`}>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {post.readingTime || 1} min read
                                </span>
                            </div>

                            {/* Tags */}
                            {post.tags && post.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-6">
                                    {post.tags.map((tag, index) => (
                                        <span 
                                            key={index}
                                            className="rounded-md border border-gray-800 px-2 py-1 text-xs text-silver-dark"
                                        >
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* Featured Image */}
                {post.featuredImage && (
                    <div className="container mx-auto px-4 sm:px-6 pb-8">
                        <div className="max-w-4xl mx-auto">
                            <img 
                                src={post.featuredImage}
                                alt={post.featuredImageAlt || post.title}
                                className="w-full h-auto rounded-xl shadow-2xl"
                            />
                        </div>
                    </div>
                )}

                {/* Article Content */}
                <div
                    ref={contentRef}
                    className={`py-8 transition-all duration-1000 transform ${contentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                >
                    <div className="container mx-auto px-4 sm:px-6">
                        <div className="max-w-3xl mx-auto">
                            {/* Blog Content - Rendered HTML with custom styling */}
                            <div 
                                className="blog-content"
                                dangerouslySetInnerHTML={{ __html: post.content }}
                            />
                            
                            {/* Custom styles for blog content */}
                            <style>{`
                                .blog-content {
                                    color: #A0A0A0;
                                    line-height: 1.8;
                                    font-size: 1.125rem;
                                }
                                .blog-content p {
                                    margin-bottom: 1.5rem;
                                    color: #A0A0A0;
                                }
                                /* Fraunces, not Georgia. The site self-hosts
                                   Fraunces for every display heading; hardcoding
                                   Georgia here meant article headings were the
                                   one place on the site rendering in a system
                                   font that ships with no other page. */
                                .blog-content h1, .blog-content h2, .blog-content h3,
                                .blog-content h4, .blog-content h5, .blog-content h6 {
                                    color: #F0F0F0;
                                    font-family: Fraunces, ui-serif, Georgia, Cambria, serif;
                                    font-weight: 500;
                                    margin-top: 2.5rem;
                                    margin-bottom: 1rem;
                                    line-height: 1.3;
                                }
                                .blog-content h1 { font-size: 2.25rem; }
                                .blog-content h2 { font-size: 1.75rem; }
                                .blog-content h3 { font-size: 1.5rem; }
                                .blog-content h4 { font-size: 1.25rem; }
                                .blog-content strong, .blog-content b {
                                    color: #F0F0F0;
                                    font-weight: 600;
                                }
                                /* moss-light / moss. These were #60A5FA and
                                   #93C5FD — raw blue hex, invisible to a search
                                   for blue-* utility classes, which is why the
                                   article body kept reading blue after the rest
                                   of the site moved to moss. */
                                .blog-content a {
                                    color: #A8D5A2;
                                    text-decoration: underline;
                                    text-underline-offset: 0.2em;
                                    transition: color 0.2s;
                                }
                                .blog-content a:hover {
                                    color: #6FA96B;
                                }
                                .blog-content ul, .blog-content ol {
                                    margin-bottom: 1.5rem;
                                    padding-left: 1.5rem;
                                }
                                .blog-content ul {
                                    list-style-type: disc;
                                }
                                .blog-content ol {
                                    list-style-type: decimal;
                                }
                                .blog-content li {
                                    margin-bottom: 0.5rem;
                                    color: #A0A0A0;
                                }
                                .blog-content li::marker {
                                    color: #6FA96B;
                                }
                                .blog-content blockquote {
                                    border-left: 3px solid #6FA96B;
                                    padding-left: 1.5rem;
                                    margin: 1.5rem 0;
                                    color: #C0C0C0;
                                    font-style: italic;
                                }
                                .blog-content code {
                                    background-color: #1A1A1A;
                                    padding: 0.125rem 0.375rem;
                                    border-radius: 0.25rem;
                                    font-size: 0.875em;
                                    color: #F0F0F0;
                                }
                                .blog-content pre {
                                    background-color: #0F1A12;
                                    border: 1px solid #2F5D3A;
                                    border-radius: 0.5rem;
                                    padding: 1rem;
                                    overflow-x: auto;
                                    margin: 1.5rem 0;
                                }
                                .blog-content pre code {
                                    background: none;
                                    padding: 0;
                                }
                                .blog-content img {
                                    border-radius: 0.75rem;
                                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
                                    margin: 1.5rem 0;
                                    max-width: 100%;
                                    height: auto;
                                }
                                .blog-content hr {
                                    border: none;
                                    border-top: 1px solid #374151;
                                    margin: 2rem 0;
                                }
                                .blog-content br {
                                    display: block;
                                    content: "";
                                    margin-top: 0.5rem;
                                }
                            `}</style>

                            {/* Share Section */}
                            <div className="mt-12 pt-8 border-t border-gray-800/50">
                                <h3 className={`text-sm font-medium ${textSecondary} mb-4`}>Share this article</h3>
                                <div className="flex gap-3">
                                    {/* Twitter/X Share */}
                                    <a
                                        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://rivieratech.solutions/blog/${post.slug}`)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg text-gray-400 hover:text-white transition-colors"
                                        aria-label="Share on Twitter"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                        </svg>
                                    </a>

                                    {/* LinkedIn Share */}
                                    <a
                                        href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`https://rivieratech.solutions/blog/${post.slug}`)}&title=${encodeURIComponent(post.title)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg text-gray-400 hover:text-white transition-colors"
                                        aria-label="Share on LinkedIn"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                        </svg>
                                    </a>

                                    {/* Copy Link */}
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(`https://rivieratech.solutions/blog/${post.slug}`);
                                            alert('Link copied to clipboard!');
                                        }}
                                        className="p-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg text-gray-400 hover:text-white transition-colors"
                                        aria-label="Copy link"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </article>

            {/* Related Posts.
                This band was bg-gradient-to-b from-black to-gray-950 and read as
                navy under the article: Tailwind's gray scale is cool-tinted, so
                gray-950 does not sit on a true-black page. Plain black with a
                hairline instead. */}
            {recentPosts.length > 0 && (
                <section className="py-16 bg-black border-t border-gray-800">
                    <div className="container mx-auto px-4 sm:px-6">
                        <div className="max-w-5xl mx-auto">
                            <h2 className={`text-2xl font-serif font-medium mb-8 ${textPrimary}`}>
                                More Articles
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {recentPosts.map(recentPost => (
                                    <Link
                                        key={recentPost._id}
                                        to={`/blog/${recentPost.slug}`}
                                        className="group rounded-lg border border-gray-800 bg-off-black p-5 transition-colors duration-300 hover:border-moss/60"
                                    >
                                        {recentPost.featuredImage && (
                                            <div className="h-32 mb-4 rounded-md overflow-hidden">
                                                <img 
                                                    src={recentPost.featuredImage}
                                                    alt={recentPost.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                        <h3 className={`mb-2 font-serif text-lg font-medium leading-snug ${textPrimary} group-hover:text-moss-light transition-colors line-clamp-2`}>
                                            {recentPost.title}
                                        </h3>
                                        <span className="text-xs text-silver-dark">
                                            {formatDate(recentPost.publishedAt)}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Back to Blog CTA */}
            <section className="py-14 bg-moss-night border-t border-moss-deep/40">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="text-center">
                        <Link
                            to="/blog"
                            className="inline-flex items-center rounded-md border border-moss/60 px-6 py-3 font-medium text-silver-light transition-colors duration-300 hover:border-moss hover:bg-moss hover:text-black"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            View All Posts
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BlogPost;
